import {
    useEffect,
    useState,
    type FC
} from "react";
import {
    Animated,
    View
} from "react-native";
import type IRadioButtonProps from "./type";
import stylesheet, {
    getRadioButtonStateStyle,
    getRadioButtonMeasures,
    getRadioButtonColors,
    getRadioButtonType,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useAnimatedState,
    useReducedMotion
} from "../../helpers/motion";
import {
    useRadioGroupContext
} from "../radioGroup/context";
import type {
    SelectionControlIndicatorStateType
} from "../selectionControl/type";
import {
    warnOnce
} from "../../utils/development";
import SelectionControl from "../selectionControl";
import StateLayer from "../stateLayer";
import Loading from "../loading";

const RadioButton: FC<IRadioButtonProps> = ({
    isDisabled: isDisabledProp = false,
    displayBehaviourWhileLoading,
    isError: isErrorProp = false,
    isChecked: isCheckedProp,
    isDefaultChecked = false,
    onPress: onPressProp,
    isLoading = false,
    type = "primary",
    onCheckedChange,
    spreadBehaviour,
    customTheme,
    value,
    ...props
}) => {
    const {
        shadowAlpha,
        elevations,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const radioGroupContext = useRadioGroupContext();

    const isGroupValue = !!radioGroupContext && value !== undefined;

    const [
        innerChecked,
        setInnerChecked
    ] = useState(isDefaultChecked);

    const isControlled = isCheckedProp !== undefined;

    const isChecked = isGroupValue ? radioGroupContext.value === value : isCheckedProp ?? innerChecked;

    const isDisabled = (radioGroupContext?.isDisabled ?? false) || isDisabledProp;

    const isError = (radioGroupContext?.isError ?? false) || isErrorProp;

    const handlePress = () => {
        if(radioGroupContext && value !== undefined) {
            radioGroupContext.onSelect(value);
        }

        if(!isChecked) {
            if(!isGroupValue && !isControlled) {
                setInnerChecked(true);
            }

            onCheckedChange?.(true);
        }

        onPressProp?.();
    };

    const onPress = isGroupValue || !isControlled || !!onCheckedChange || !!onPressProp ? handlePress : undefined;

    const isReduceMotionEnabled = useReducedMotion();

    const dotScale = useAnimatedState({
        value: isChecked ? 1 : 0,
        intent: "toggle",
        customTheme
    });

    const currentType = getRadioButtonType({
        colors,
        type
    });

    const currentMeasures = getRadioButtonMeasures({
        iconSizes,
        radiuses,
        borders
    });

    const currentColors = getRadioButtonColors({
        currentType,
        isDisabled,
        isChecked,
        isError,
        colors
    });

    const {
        ring: ringDynamicStyle,
        dot: dotDynamicStyle
    } = useStyles({
        currentMeasures,
        currentColors,
        shadowAlpha,
        elevations,
        isDisabled,
        colors
    });

    useEffect(() => {
        if(displayBehaviourWhileLoading === undefined) {
            return;
        }

        warnOnce({
            message: "RadioButton: \"displayBehaviourWhileLoading\" is deprecated and will be removed in the next major version. A loading RadioButton keeps its type colour and always blocks presses."
        });
    }, [
        displayBehaviourWhileLoading
    ]);

    const renderDot = () => {
        return <Animated.View
            style={[
                dotDynamicStyle,
                {
                    transform: [
                        {
                            scale: dotScale
                        }
                    ]
                }
            ]}
        />;
    };

    const renderIndicator = ({
        isFocusVisible,
        isHovered,
        isPressed
    }: SelectionControlIndicatorStateType) => {
        const {
            loading: loadingStateStyle,
            ring: ringStateStyle
        } = getRadioButtonStateStyle({
            isReduceMotionEnabled,
            currentMeasures,
            isFocusVisible,
            currentColors,
            durations,
            isHovered,
            isPressed,
            borders,
            colors,
            type
        });

        if(isLoading) {
            return <Loading
                customColor={currentColors.spinnerColor}
                size={currentMeasures.ringSize}
                customTheme={customTheme}
                style={loadingStateStyle}
                aria-hidden={true}
            />;
        }

        return <View
            style={[
                stylesheet.ring,
                ringDynamicStyle,
                ringStateStyle
            ]}
        >
            <StateLayer
                borderRadius={currentMeasures.borderRadius}
                borderWidth={currentMeasures.borderWidth}
                overlayColor={currentColors.overlayColor}
                customTheme={customTheme}
                isHovered={isHovered}
                isPressed={isPressed}
            />
            {renderDot()}
        </View>;
    };

    return <SelectionControl
        {...props}
        renderIndicator={renderIndicator}
        spreadBehaviour={spreadBehaviour}
        customTheme={customTheme}
        checkedState={isChecked}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onPress={onPress}
        isError={isError}
        role="radio"
    />;
};
export default RadioButton;
